import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';

const useTaskCompliance = (useruuid, userTimezone, selectedMonth, extraPriorDays = 0) => {
  const scheduleApi = useSelector(state => state.apis.scheduleApi);
  const trans = useSelector(translator);
  const transRef = useRef(trans);
  useEffect(() => { transRef.current = trans; }, [trans]);

  const [complianceData, setComplianceData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Stable string key to avoid re-fetching when moment reference changes
  const monthKey = selectedMonth ? selectedMonth.format('YYYY-MM') : null;

  const processComplianceData = useCallback(
    tasks => {
      if (!tasks || tasks.length === 0) {
        return {
          totalTasks: 0,
          completedTasks: 0,
          missedTasks: 0,
          ongoingTasks: 0,
          complianceRate: 0,
          dailyTasks: [],
          lastMissed: null,
          taskComplianceByName: {},
        };
      }

      // Filter only task events (API may return 'task' singular or 'tasks' plural)
      const taskEvents = tasks.filter(
        event => event.event_type === 'tasks' || event.event_type === 'task'
      );

      // Calculate overall stats
      const totalTasks = taskEvents.length;
      const completedTasks = taskEvents.filter(t => t.task_status === 'completed').length;
      const missedTasks = taskEvents.filter(t => t.task_status === 'expired').length;
      const ongoingTasks = taskEvents.filter(t => t.task_status === 'ongoing').length;
      const complianceRate = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

      // Calculate per-task compliance (by task name)
      const taskComplianceByName = {};
      taskEvents.forEach(task => {
        const taskName = task.event_name;
        if (!taskComplianceByName[taskName]) {
          taskComplianceByName[taskName] = {
            total: 0,
            completed: 0,
            missed: 0,
            rate: 0,
          };
        }
        taskComplianceByName[taskName].total++;
        if (task.task_status === 'completed') {
          taskComplianceByName[taskName].completed++;
        } else if (task.task_status === 'expired') {
          taskComplianceByName[taskName].missed++;
        }
      });

      // Calculate compliance rate for each task
      Object.keys(taskComplianceByName).forEach(taskName => {
        const task = taskComplianceByName[taskName];
        task.rate = task.total > 0 ? (task.completed / task.total) * 100 : 0;
      });

      // Group tasks by date
      const tasksByDate = {};
      taskEvents.forEach(task => {
        const date = moment(task.event_timestamp).tz(userTimezone).format('YYYY-MM-DD');
        if (!tasksByDate[date]) {
          tasksByDate[date] = [];
        }
        const taskCompliance = taskComplianceByName[task.event_name];
        tasksByDate[date].push({
          name: task.event_name,
          status: task.task_status,
          time: moment(task.event_timestamp).tz(userTimezone).format('h:mm A'),
          timestamp: task.event_timestamp,
          compliance: taskCompliance,
          category: task.category ?? null,
        });
      });

      // Convert to daily tasks array (sorted by date)
      const dailyTasks = Object.keys(tasksByDate)
        .sort()
        .map(date => ({
          date,
          tasks: tasksByDate[date].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)),
        }));

      // Find last missed task
      const missedTasksList = taskEvents
        .filter(t => t.task_status === 'expired')
        .sort((a, b) => new Date(b.event_timestamp) - new Date(a.event_timestamp));

      const lastMissed =
        missedTasksList.length > 0
          ? {
              date: missedTasksList[0].event_timestamp,
              name: missedTasksList[0].event_name,
              scheduledTime: moment(missedTasksList[0].event_timestamp)
                .tz(userTimezone)
                .format('h:mm A'),
            }
          : null;

      return {
        totalTasks,
        completedTasks,
        missedTasks,
        ongoingTasks,
        complianceRate,
        dailyTasks,
        lastMissed,
        taskComplianceByName,
      };
    },
    [userTimezone]
  );

  const fetchComplianceData = useCallback(async () => {
    if (!useruuid || !scheduleApi || !userTimezone || !monthKey) {
      setComplianceData(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const monthStart = moment.tz(monthKey, 'YYYY-MM', userTimezone).startOf('month');
      const fetchStart = extraPriorDays > 0
        ? monthStart.clone().subtract(extraPriorDays, 'days')
        : monthStart;
      const monthEnd = monthStart.clone().endOf('month');

      const startDateTime = fetchStart.toISOString();
      const endDateTime = monthEnd.toISOString();

      const events = await new Promise((resolve, reject) => {
        scheduleApi.eventsByEventTypesTimeRange(
          useruuid,
          startDateTime,
          endDateTime,
          'task',
          { timezone: userTimezone, status: true },
          apiCallback({
            translator: transRef.current,
            failCallback: reject,
            errorCallback: reject,
            successCallback: data => resolve(data.schedule_events || []),
          })
        );
      });

      const processed = processComplianceData(events);
      setComplianceData(processed);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [useruuid, scheduleApi, userTimezone, monthKey, extraPriorDays, processComplianceData]);

  useEffect(() => {
    fetchComplianceData();
  }, [fetchComplianceData]);

  return {
    complianceData,
    isLoading,
    error,
    refetch: fetchComplianceData,
  };
};

export default useTaskCompliance;
